import { toInterfaceArray } from '../helpers/helpers';
import {Food, IMealFood, MealFood} from './food';
import {FoodProduct, IMealFoodProduct, MealFoodProduct} from './FoodProduct';
import { MealItem, MealItemType } from './MealItem';
import {IMealRecipe, IRecipe, MealRecipe, Recipe} from './Recipe';
import {TypicalMeal} from './User';
import {CategoryStatistics, SingleCategoryStatistic} from './Statistics';

export class Meal {
    private readonly _typicalMeal: TypicalMeal;
    type: MealType;
    get displayName(): string { return mealDisplayNames.get(this.type); }
    get icon(): string { return mealIcons.get(this.type); }
    get specifity(): MealSpecifity { return this.getMealSpecifity(); }
    get calories(): number { return this.fastCalories + this.detailedCalories; }
    get emissions(): number { return this.fastEmissions + this.detailedEmissions; }
    fastCalories: number;
    fastEmissions: number;
    get detailedCalories(): number { return this.getDetailedCalories(); }
    get detailedEmissions(): number { return this.getDetailedEmissions(); }
    get typicalCalories(): number { return this._typicalMeal.typicalCalories; }
    itemList: MealItem[] = [];
    private get itemCount(): number { return this.itemList.length; }
    get hasItems(): boolean { return this.itemCount > 0; }
    get hasFastTracker(): boolean { return this.specifity === MealSpecifity.combined || this.specifity === MealSpecifity.fast; }
    get isEmpty(): boolean { return !this.hasItems && !this.fastEmissions; }
    get edited(): boolean { return !!this.fastEmissions || !this.onlyContainsTypicalItems(); }
    get foodCount(): number { return this.itemList.filter(i => i.type === MealItemType.food).length; }
    get recipeCount(): number { return this.itemList.filter(i => i.type === MealItemType.recipe).length; }
    get foodProductCount(): number { return this.itemList.filter(i => i.type === MealItemType.foodProduct).length; }
    get foodAndProductCount(): number { return this.foodCount + this.foodProductCount; }

    // Disabling meals not possible anymore, because fast tracker not used. For backward compatibility reasons and to
    // easily activate it again it remains there as a getter which always returns true
    private _disabled = false;
    get disabled(): boolean { return false; }
    set disabled(value: boolean) { this._disabled = value; }
    get carbs(): number { return this.hasItems ? this.itemList.reduce((p: number, c) => p + (c.carbsKnown ? c.carbs : 0), 0) : 0; }
    get fat(): number { return this.hasItems ? this.itemList.reduce((p: number, c) => p + (c.fatKnown ? c.fat : 0), 0) : 0; }
    get protein(): number { return this.hasItems ? this.itemList.reduce((p: number, c) => p + (c.proteinKnown ? c.protein : 0), 0) : 0; }
    get macrosKnown(): boolean { return this.hasItems ? this.itemList.every(i => i.macrosKnown) : true; }
    get amountInGram(): number { return this.hasItems ? this.itemList.reduce((p: number, c) => p + c.amountInGram, 0) : 0; }
    private get amountIn100g(): number { return this.amountInGram / 100; }
    get carbsPer100g(): number { return this.amountIn100g ? this.carbs / this.amountIn100g : 0; }
    get fatPer100g(): number { return this.amountIn100g ? this.fat / this.amountIn100g : 0; }
    get proteinPer100g(): number { return this.amountIn100g ? this.protein / this.amountIn100g : 0; }
    get caloriesPer100g(): number { return this.amountIn100g ? this.calories / this.amountIn100g : 0; }

    get categoryStatisticsEmissions(): CategoryStatistics {
        const ret = new CategoryStatistics();
        this.itemList.forEach(item => {
            const statistics = item.categoryStatisticsEmissions;
            if (statistics instanceof SingleCategoryStatistic) {
                ret.add(statistics.toCategoryStatistic());
            } else {
                ret.addMany(statistics.categories);
            }
        });
        return ret;
    }

    get categoryStatisticsCalories(): CategoryStatistics {
        const ret = new CategoryStatistics();
        this.itemList.forEach(item => {
            const statistics = item.categoryStatisticsCalories;
            if (statistics instanceof SingleCategoryStatistic) {
                ret.add(statistics.toCategoryStatistic());
            } else {
                ret.addMany(statistics.categories);
            }
        });
        return ret;
    }

    constructor(i?: IMeal, typicalMeal?: TypicalMeal, recipes?: Recipe[]) {
        // temporary loud failing until all places with new meals are resolved
        if (!i && !typicalMeal) {
            throw new Error('Meals must be created with either an interface or a typical meal!');
        }

        this.fastCalories = 0;
        this.fastEmissions = 0;

        if (i){
            if (i.typicalMeal) { this._typicalMeal = i.typicalMeal; }
            this.type = i.type;
            this.fastCalories = i.fastCalories;
            this.fastEmissions = i.fastEmissions;
            if (i.foodList){
                i.foodList.forEach(food => {
                    this.itemList.push(new MealItem(new MealFood(food)));
                });
            }
            if (i.recipeList) {
                // This has additional logic because recipe portions which are saved in the backend override the portions
                // of the original recipe
                i.recipeList.forEach(recipe => {
                    if (recipes) {
                        const existingRecipe = recipes.find(r => r.id === recipe.id);
                        if (existingRecipe) {
                            const newMealRecipe = new MealRecipe(existingRecipe);
                            newMealRecipe.amount = recipe.portions;
                            this.itemList.push(new MealItem(newMealRecipe));
                        } else {
                            this.itemList.push(new MealItem(new Recipe(recipe)));
                        }
                    } else {
                        this.itemList.push(new MealItem(new Recipe(recipe)));
                    }
                });
            }
            if (i.mealRecipeList) {
                i.mealRecipeList.forEach(mealRecipe => {
                    this.itemList.push(new MealItem(new MealRecipe(mealRecipe)));
                });
            }
            if (i.foodProductList){
                i.foodProductList.forEach(foodProduct => {
                    this.itemList.push(new MealItem(new MealFoodProduct(foodProduct)));
                });
            }
            this._disabled = i.disabled;
        }
        if (typicalMeal) {
            this._typicalMeal = typicalMeal;
            this.type = typicalMeal.type;
            this._disabled = typicalMeal.disabled;
            if (typicalMeal.typicalMealItems) {
                typicalMeal.typicalMealItems.forEach(item => this.itemList.push(MealItem.fromDetailedInterface(item)));
            }
        }
    }

    public static EmptyBreakfast(): Meal {
        return new Meal(undefined, {type: MealType.Breakfast});
    }

    public static EmptyLunch(): Meal {
        return new Meal(undefined, {type: MealType.Lunch});
    }

    public static EmptyDinner(): Meal {
        return new Meal(undefined, {type: MealType.Dinner});
    }

    public static EmptySnack(): Meal {
        return new Meal(undefined, {type: MealType.Snack});
    }

    public deleteItem(index: number) {
        this.itemList.splice(index, 1);
    }

    public resetFastEntry() {
        this.fastCalories = 0;
        this.fastEmissions = 0;
    }

    public resetDetailedEntries(): MealItem[] {
        const removedItems: MealItem[] = [];
        this.itemList.forEach(element => {
            removedItems.push(element);
        });
        this.itemList = [];
        return removedItems;
    }

    public importMeal(importMeal: Meal, overwrite = false): void {
        if (importMeal.disabled) { return; }
        if (overwrite) { this.itemList = []; } // fast tracker will always be overwritten
        this._disabled = importMeal.disabled;
        this.fastEmissions = importMeal.fastEmissions;
        this.fastCalories = importMeal.fastCalories;
        if (importMeal.itemList && importMeal.itemList.length > 0) {
            importMeal.itemList.forEach(item => this.itemList.push(item));
        }
    }

    public updateMacros(foods: Food[], foodProducts: FoodProduct[]): void {
        if (!this.hasItems) { return; }
        this.itemList.forEach(item => {
            if (!item.macrosKnown) { item.updateMacros(foods, foodProducts); }
        });
    }

    public toInterface(): IMeal {
        const foodList: MealFood[] = [];
        const mealRecipeList: MealRecipe[] = [];
        const foodProductList: MealFoodProduct[] = [];
        this.itemList.forEach(item => {
            const specifiedItem = item.toDetailedMealItem();
            if (specifiedItem instanceof MealFood) { foodList.push(specifiedItem); }
            if (specifiedItem instanceof MealRecipe) { mealRecipeList.push(specifiedItem); }
            if (specifiedItem instanceof MealFoodProduct) { foodProductList.push(specifiedItem); }
        });
        return {type: this.type, fastCalories: this.fastCalories, fastEmissions: this.fastEmissions,
            foodList: toInterfaceArray(foodList), recipeList: undefined, mealRecipeList: toInterfaceArray(mealRecipeList),
            foodProductList: toInterfaceArray(foodProductList), disabled: this.disabled, typicalMeal: this._typicalMeal};
    }

    private getDetailedCalories(): number {
        let ret = 0;
        this.itemList.forEach(item => {
            if (item) { ret += item.calories; }
        });
        return ret;
    }

    private getDetailedEmissions(): number {
        let ret = 0;
        this.itemList.forEach(item => {
            if (item) { ret += item.emissions; }
        });
        return ret;
    }

    private getMealSpecifity(): MealSpecifity {
        if (this.fastCalories > 0 && this.itemList.length > 0) { return MealSpecifity.combined; }
        if (this.fastCalories > 0) { return MealSpecifity.fast; }
        if (this.itemList.length > 0) { return MealSpecifity.detailed; }
        return MealSpecifity.unspecified;
    }

    private onlyContainsTypicalItems(): boolean {
        if (!this._typicalMeal || !this._typicalMeal.typicalMealItems) { return !this.hasItems; }
        const typicalMealItems = MealItem.fromDetailedInterfaceArray(this._typicalMeal.typicalMealItems);
        for (const item of this.itemList) {
            const found = typicalMealItems.find(i => i.equals(item));
            if (!found) { return false; }
        }
        return true;
    }
}

export interface IMeal {
    typicalMeal: TypicalMeal;
    type: MealType;
    fastCalories: number;
    fastEmissions: number;
    foodList: IMealFood[];
    // not used anymore, mealRecipeList is used instead
    recipeList: IRecipe[];
    mealRecipeList: IMealRecipe[];
    foodProductList: IMealFoodProduct[];
    disabled: boolean;
}

export enum MealType {
    Breakfast,
    Lunch,
    Dinner,
    Snack
}

export enum MealSpecifity {
    unspecified,
    fast,
    detailed,
    combined
}

export const mealIcons = new Map<MealType, string>([
    [MealType.Breakfast, '/assets/icon/meals/breakfast.svg'],
    [MealType.Lunch, '/assets/icon/meals/lunch.svg'],
    [MealType.Dinner, '/assets/icon/meals/dinner.svg'],
    [MealType.Snack, '/assets/icon/meals/snack.svg']
]);

export const mealColors = new Map<MealType, string>([
    [MealType.Breakfast, 'var(--ion-color-blue)'],
    [MealType.Lunch, 'var(--ion-color-primary)'],
    [MealType.Dinner, 'var(--ion-color-orange)'],
    [MealType.Snack, 'var(--ion-color-red)']
]);

export const mealDisplayNames = new Map<MealType, string>([
    [MealType.Breakfast, 'Frühstück'],
    [MealType.Lunch, 'Mittagessen'],
    [MealType.Dinner, 'Abendessen'],
    [MealType.Snack, 'Snacks']
]);
